import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import styles from './ProductSearchInput.module.scss';

interface BaseProduct {
  id: string;
  name: string;
  price: number | string;
  description?: string;
}

interface ProductSearchInputProps {
  products: BaseProduct[];
  value: string;
  onChange: (productId: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  excludeProductIds?: string[]; // Products to exclude from results
  filter?: (product: BaseProduct) => boolean; // Optional filter function
}

function ProductSearchInput({
  products,
  value,
  onChange,
  placeholder = 'Search for a product...',
  required = false,
  disabled = false,
  excludeProductIds = [],
  filter,
}: ProductSearchInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Find selected product
  const selectedProduct = products.find((p) => p.id === value);

  // Filter products
  const filteredProducts = products.filter((product) => {
    // Exclude products in excludeProductIds
    if (excludeProductIds.includes(product.id)) {
      return false;
    }

    // Apply custom filter if provided
    if (filter && !filter(product)) {
      return false;
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return (
        product.name.toLowerCase().includes(query) ||
        (product.description?.toLowerCase().includes(query) ?? false)
      );
    }

    return true;
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Handle input focus
  const handleFocus = () => {
    setIsOpen(true);
    if (!value) {
      setSearchQuery('');
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsOpen(true);
    setHighlightedIndex(-1);

    // Clear selection if user is typing
    if (value && query !== selectedProduct?.name) {
      onChange('');
    }
  };

  // Handle product selection
  const handleSelect = (productId: string) => {
    onChange(productId);
    setSearchQuery('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  // Handle clear button
  const handleClear = () => {
    onChange('');
    setSearchQuery('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || filteredProducts.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredProducts.length - 1 ? prev + 1 : prev,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredProducts.length
        ) {
          handleSelect(filteredProducts[highlightedIndex].id);
        } else if (filteredProducts.length === 1) {
          handleSelect(filteredProducts[0].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Display value: show product name if selected, otherwise show search query
  const displayValue = selectedProduct ? selectedProduct.name : searchQuery;

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.inputWrapper}>
        <Search size={16} className={styles.searchIcon} />
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={styles.input}
          autoComplete="off"
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={styles.clearButton}
            aria-label="Clear selection"
          >
            <X size={16} />
          </button>
        )}
      </div>
      {isOpen && filteredProducts.length > 0 && (
        <div className={styles.suggestions}>
          {filteredProducts.map((product, index) => (
            <button
              key={product.id}
              type="button"
              onClick={() => handleSelect(product.id)}
              className={`${styles.suggestionItem} ${
                index === highlightedIndex ? styles.highlighted : ''
              }`}
            >
              <div className={styles.productInfo}>
                <span className={styles.productName}>{product.name}</span>
                {product.description && (
                  <span className={styles.productDescription}>
                    {product.description}
                  </span>
                )}
                <span className={styles.productPrice}>
                  $
                  {typeof product.price === 'string'
                    ? Number(product.price).toFixed(2)
                    : product.price.toFixed(2)}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProductSearchInput;
